import { useState } from "react";
import { RichTextRenderer } from "~/components/RichTextRenderer";
import { useAuthentication } from "~/lib/better-auth/client";
import { Check } from "lucide-react";

interface FeatureItem {
  name: string;
  values: Array<string | number | boolean | null>;
}

interface FeatureCategory {
  name: string;
  features: FeatureItem[];
}

interface Plan {
  key?: string;
  name: string;
  halfYearPrice: number;
  yearlyPrice: number;
  description?: string;
}

interface PricingTableProps {
  plans: Plan[];
  featureCategories: FeatureCategory[];
  mode?: "cta" | "selection";
  selectedPlanKey?: string;
  onSelectPlanKey?: (key: string) => void;
  selectionDisabled?: boolean;
  billingCycle?: "half-year" | "full-year";
  onBillingCycleChange?: (value: "half-year" | "full-year") => void;
}

const ADVERTISER_ADD_LOCATION_PATH = "/add-location";
const ADVERTISER_SIGN_UP_PATH = "/sign-up";

export function PricingTable({
  plans,
  featureCategories,
  mode = "cta",
  selectedPlanKey,
  onSelectPlanKey,
  selectionDisabled = false,
  billingCycle: controlledBillingCycle,
  onBillingCycleChange,
}: PricingTableProps) {
  const [uncontrolledBillingCycle, setUncontrolledBillingCycle] = useState<
    "half-year" | "full-year"
  >("half-year");
  const billingCycle = controlledBillingCycle ?? uncontrolledBillingCycle;
  const setBillingCycle = (value: "half-year" | "full-year") => {
    if (controlledBillingCycle === undefined) {
      setUncontrolledBillingCycle(value);
    }
    onBillingCycleChange?.(value);
  };
  const { isAuthenticated } = useAuthentication();
  const advertiserBaseUrl = import.meta.env.VITE_ADMIN_URL ?? "";
  const ctaHref = isAuthenticated
    ? `${advertiserBaseUrl}${ADVERTISER_ADD_LOCATION_PATH}`
    : `${advertiserBaseUrl}${ADVERTISER_SIGN_UP_PATH}?redirect=${encodeURIComponent(ADVERTISER_ADD_LOCATION_PATH)}`;

  return (
    <div className="layout-place-full">
      {/* Billing toggle */}
      <div className="flex justify-center mb-6">
        <div className="p-1 bg-black rounded">
          <button
            type="button"
            className={`px-4 py-2 rounded-md ${
              billingCycle === "half-year"
                ? "bg-primary text-black"
                : "bg-black text-white"
            }`}
            onClick={() => setBillingCycle("half-year")}
          >
            Halbjährlich
          </button>
          <button
            type="button"
            className={`px-4 py-2 rounded-md ${
              billingCycle === "full-year"
                ? "bg-primary text-black"
                : "bg-black text-white"
            }`}
            onClick={() => setBillingCycle("full-year")}
          >
            Jährlich
          </button>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full table-fixed">
          {/* Header: Pricing Cards */}
          <thead>
            <tr>
              <th></th>
              {plans.map((plan, index) => (
                <th key={plan.name} className="px-4 py-4 w-1/4 text-center">
                  <div className="flex flex-col text-left gap-2">
                    <span className="text-lg font-medium">{plan.name}</span>
                    <span className="text-6xl w-fit">
                      €
                      {billingCycle === "half-year"
                        ? plan.halfYearPrice
                        : plan.yearlyPrice}
                    </span>
                    <span className="text-sm">
                      {billingCycle === "half-year"
                        ? "Pro Halbjahr"
                        : "Pro Jahr"}
                    </span>
                    {plan.description && (
                      <RichTextRenderer
                        additionalClasses="text-sm"
                        richText={plan.description}
                      />
                    )}
                    {mode === "selection" ? (
                      (() => {
                        const key = plan.key ?? String(index);
                        const isSelected = selectedPlanKey === key;
                        return (
                          <button
                            type="button"
                            disabled={selectionDisabled}
                            onClick={() => onSelectPlanKey?.(key)}
                            className={`mt-2 inline-flex items-center justify-center gap-2 px-4 py-2 rounded font-medium border transition-colors ${
                              isSelected
                                ? "bg-black text-white border-black"
                                : "bg-white text-black border-black"
                            } ${
                              selectionDisabled
                                ? "opacity-60 cursor-not-allowed"
                                : "hover:bg-black hover:text-white cursor-pointer"
                            }`}
                          >
                            <span
                              className={`size-5 border rounded-full flex items-center justify-center ${
                                isSelected
                                  ? "border-white bg-white/20"
                                  : "border-black"
                              }`}
                            >
                              {isSelected ? (
                                <Check className="size-3.5 text-white" />
                              ) : null}
                            </span>
                            {isSelected ? "Ausgewählt" : "Auswählen"}
                          </button>
                        );
                      })()
                    ) : (
                      <a
                        href={ctaHref}
                        className="mt-2 inline-block bg-primary px-4 py-2 rounded text-center font-medium text-black no-underline hover:bg-primary/90"
                      >
                        Starten
                      </a>
                    )}
                  </div>
                </th>
              ))}
            </tr>
          </thead>

          {/* Feature Categories */}
          {featureCategories.map((category, catIdx) => (
            <tbody key={catIdx}>
              {/* Kategorie Titel */}
              <tr>
                <td
                  colSpan={plans.length + 1}
                  className="py-4 font-bold text-left"
                >
                  {category.name}
                </td>
              </tr>

              {/* Features dieser Kategorie */}
              {category.features.map((feature, idx) => (
                <tr key={idx}>
                  <td className="border-2 border-dove-gray-light px-0 py-2 font-medium border-l-0">
                    {feature.name}
                  </td>
                  {feature.values.map((val, i) => (
                    <td
                      key={i}
                      className="border-2 border-dove-gray-light px-4 py-2 text-center last:border-r-0"
                    >
                      {typeof val === "boolean" ? (val ? "✔" : "✘") : val}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          ))}
        </table>
      </div>
    </div>
  );
}
